/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import getConfig from '../../config';
import https from 'https';
import { CCHomeAppsAppPickerUtil, CCHomeAppsUtil, singleAppPlans } from 'cchome-apps';
import { DeviceDetectionUtil } from 'cchome-shared/utils';

class FloodgateService {
	// Singleton
	static instance: FloodgateService;

	constructor() {
		if (!FloodgateService.instance) {
			FloodgateService.instance = this;
		}
		return FloodgateService.instance;
	}

	_getPlanUrl() {
		let workflowUrl = window.location.pathname.slice(1);
		let planName = CCHomeAppsAppPickerUtil.getAppPickerPlan();
		if (['ccpp', 'ccppn', 'all-apps'].indexOf(planName) > -1) {
			// All Apps, CCPP, CCPPN Plan Post-Purchase Case
			workflowUrl = `apps/starter/${planName}`;
		} else {
			planName = CCHomeAppsUtil.getSingleAppPostPurchasePlan();
			if (singleAppPlans.includes(planName)) {
				// Single App Plan Post-Purchase Case
				workflowUrl = `apps/starter/${planName}`;
			}
		}
		return workflowUrl;
	}

	/**
	 * This method return the string with the context params
	 * @param {string} clientLanguage The current client language code for CC Home.
	 * @param {string} clientCountry The current client country code for CC Home.
	 */
	_getContextParamsString(clientLanguage, clientCountry) {
		let userOsType;
		if (DeviceDetectionUtil.isWin()) {
			userOsType = 'win';
		} else if (DeviceDetectionUtil.isMacOS() && !DeviceDetectionUtil.isTouchDevice()) {
			userOsType = 'mac';
		} else {
			userOsType = 'other';
		}
		const userOsVersion = DeviceDetectionUtil.getOSVersion();
		const userOsVersionArr = userOsVersion.split('.');
		const userOsMajorVersion = userOsVersionArr && userOsVersionArr[0];
		const userOsMinorVersion = userOsVersionArr && (userOsVersionArr[1] || 0);
		const userOsSubMinorVersion = userOsVersionArr && (userOsVersionArr[2] || 0);
		const workflowUrl = this._getPlanUrl();
		return `cchClientLanguage=${clientLanguage}&userImsCountry=${clientCountry}&userLanguage=${clientLanguage}&userOsType=${userOsType}&userOsMajorVersion=${userOsMajorVersion}&userOsMinorVersion=${userOsMinorVersion}&userOsSubMinorVersion=${userOsSubMinorVersion}&workflowUrl=${workflowUrl}`;
	}

	/**
	 * This method calls the Floodgate Features API to retrieve the list of features and releases for the user. See https://wiki.corp.adobe.com/pages/viewpage.action?pageId=1308897197
	 * @param {string} clientLanguage The current client language code for CC Home.
	 * @param {string} clientCountry The current client country code for CC Home.
	 */
	_getFeatures(clientLanguage, clientCountry) {
		let timeout: number;
		return new Promise<FloodgateServiceResponse>((resolve, reject) => {
			const requestHeaders: RequestHeaders = {
				'x-api-key': getConfig('CLIENT_ID'),
				'Content-Type': 'application/json',
				Accept: 'application/json',
			};
			if (window.adobeIMS && window.adobeIMS.getAccessToken()) {
				requestHeaders.Authorization = `Bearer ${window.adobeIMS.getAccessToken()}`;
			}
			const request = https.request(
				{
					hostname: getConfig('SERVICES').fg,
					path: `/api/v3/feature/?clientId=${getConfig(
						'CLIENT_ID',
					)}&meta=false&${this._getContextParamsString(clientLanguage, clientCountry)}`,
					headers: requestHeaders,
				},
				(res) => {
					window.clearTimeout(timeout);
					// If there is an error, reject with error.
					if (res.statusCode && res.statusCode >= 400) {
						window.cchome.util.logger.error(
							'Floodgate Service',
							'Floodgate Service responded in error',
							res,
						);
						const floodgateError = new Error(`Floodgate Service responded in error: ${res.statusCode}`);
						reject(floodgateError);
					} else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', (chunk) => (data += chunk));
						res.on('end', () => {
							try {
								resolve(JSON.parse(data));
							} catch (e) {
								// If there is a parsing error, reject with error.
								window.cchome.util.logger.error(
									'Floodgate Service',
									'Floodgate Service parsing error',
									e.toString(),
								);
								const floodgateServiceError = new Error('Floodgate Service parsing error');
								reject(floodgateServiceError);
							}
						});
					}
				},
			);
			timeout = window.setTimeout(() => {
				window.cchome.util.logger.error('Floodgate Service', 'Floodgate Service timed out');
				request.abort();
				clearTimeout(timeout);
				const floodgateServiceError = new Error('Floodgate Service timed out');
				reject(floodgateServiceError);
			}, 5000);
			request.on('error', (err) => {
				clearTimeout(timeout);
				// If there is a connection error, reject with error.
				window.cchome.util.logger.error('Floodgate Service', 'Floodgate Service connect error', err.toString());
				const floodgateServiceError = new Error('Floodgate Service connect error');
				reject(floodgateServiceError);
			});
			request.end();
		});
	}

	/**
	 * This method looks for active release and adds the feature flags from release to cchome.config.features
	 * @param {JSON object} data The JSON response of the floodgate call
	 */
	_addFeaturesFromRelease(data: FloodgateServiceResponse) {
		for (const i in getConfig('FG_CONFIG').releases) {
			const configRelease = getConfig('FG_CONFIG').releases[i];
			for (const j in data.releases) {
				const fgRelease = data.releases[j];
				if (fgRelease.release_name === configRelease.name) {
					// add feature flag in to cchome.config.features for use in sub modules
					if (fgRelease.features) {
						const newFeatureArr = window.cchome.config.features.concat(fgRelease.features);
						window.cchome.config.features = newFeatureArr;
						window.cchome.config.featuresAnalyticsParams = window.cchome.config.featuresAnalyticsParams.concat(
							fgRelease.release_analytics_params,
						);
					}
					break;
				}
			}
		}
	}

	addFeatureFlagsFromActiveReleases(language, clientCountry) {
		window.cchome.config.featuresAnalyticsParams = [];
		return new Promise((resolve) => {
			if (
				getConfig('FG_CONFIG') &&
				getConfig('FG_CONFIG').releases &&
				getConfig('FG_CONFIG').releases.length > 0
			) {
				// If no releases mentioned in config, no need to make a call to floodgate
				const clientLanguage = language ? language : 'en';
				this._getFeatures(clientLanguage, clientCountry).then(
					(data) => {
						// If there are any releases, check if any of them match the ones in our config and add the flags from it.
						if (data && data.releases && data.releases.length) {
							this._addFeaturesFromRelease(data);
						}
						resolve(null);
					},
					(error) => {
						window.cchome.config.isFGServiceFail = true;
						window.cchome.util.logger.error(
							'Floodgate Service',
							'Floodgate error returned',
							error.toString(),
						);
						resolve(null);
					},
				);
			} else {
				resolve(null);
			}
		});
	}
}

export default FloodgateService;
